import { supabase } from '@/integrations/supabase/client';

/**
 * Centralized API client for handling all external API calls
 * Provides consistent patterns for authentication, error handling, and request management
 */
export class ApiClient {
  private static instance: ApiClient;
  
  private constructor() {}
  
  /**
   * Get singleton instance of the API client
   */
  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Get current authentication headers
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No valid session found. Please log in again.');
    }

    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get Supabase project ID from the current URL
   */
  private getProjectId(): string {
    const supabaseUrl = (globalThis as any).import?.meta?.env?.VITE_SUPABASE_URL;
    const projectId = supabaseUrl?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
    
    if (!projectId) {
      throw new Error('Could not determine Supabase project ID');
    }
    
    return projectId;
  }

  /**
   * Call a Supabase Edge Function with proper authentication and error handling
   */
  async callEdgeFunction(
    functionName: string, 
    options: {
      body?: any;
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      headers?: Record<string, string>;
    } = {}
  ): Promise<{ data: any; error: any }> {
    try {
      const { method = 'POST', body, headers = {} } = options;
      const projectId = this.getProjectId();
      const functionUrl = `https://${projectId}.functions.supabase.co/${functionName}`;
      
      const authHeaders = await this.getAuthHeaders();
      const requestHeaders: Record<string, string> = {
        ...authHeaders,
        ...headers
      };

      const response = await fetch(functionUrl, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If parsing fails, use the raw text or default message
          errorMessage = errorText || errorMessage;
        }
        
        return {
          data: null,
          error: {
            message: errorMessage,
            status: response.status,
            statusText: response.statusText
          }
        };
      }

      const responseData = await response.json();
      return {
        data: responseData,
        error: null
      };

    } catch (error) {
      console.error(`❌ Error calling edge function ${functionName}:`, error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          originalError: error
        }
      };
    }
  }

  /**
   * Call a Supabase Edge Function using the Supabase client (preferred method)
   * This is more reliable and handles authentication automatically
   */
  async invokeEdgeFunction(
    functionName: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
    } = {}
  ): Promise<{ data: any; error: any }> {
    try {
      const { body, headers = {} } = options;
  
      console.log("Calling edge function with:", { body, headers });
      
      // Don't add auth headers - supabase.functions.invoke handles this automatically
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
        headers // Only pass custom headers, not auth headers
      });
  
      return { data, error };
  
    } catch (error) {
      console.error(`❌ Error invoking edge function ${functionName}:`, error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          originalError: error
        }
      };
    }
  }

  /**
   * Make a generic HTTP request with authentication
   */
  async request(
    url: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: any;
      headers?: Record<string, string>;
    } = {}
  ): Promise<{ data: any; error: any }> {
    try {
      const { method = 'GET', body, headers = {} } = options;
      const authHeaders = await this.getAuthHeaders();
      
      const requestHeaders: Record<string, string> = {
        ...authHeaders,
        ...headers
      };

      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        return {
          data: null,
          error: {
            message: errorMessage,
            status: response.status,
            statusText: response.statusText
          }
        };
      }

      const responseData = await response.json();
      return {
        data: responseData,
        error: null
      };

    } catch (error) {
      console.error(`❌ Error making request to ${url}:`, error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          originalError: error
        }
      };
    }
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();